import { memo, useEffect, useRef, useState } from 'react'
import type { NodeProps } from '@xyflow/react'
import { Handle, Position } from '@xyflow/react'
import { changesIn, normaliseGrade, type GradeSettings } from './grade/gradeSettings'
import { SkillGlyph } from '@shared/components/SkillGlyph'

/**
 * The Photo adjust card (Huanrui, 2026-10-15; Colour grade until 2026-10-30)
 * -------------------------------------------------------------
 * Camera Raw's colour work, and its Geometry, on the board: pictures wired in on the left,
 * graded copies out on the right. Free, done in this browser on the
 * graphics card (grade/gradeEngine.ts).
 *
 *  - Adjust opens the full-screen studio (grade/GradeStudio.tsx) on the
 *    first picture wired in; the others are a click away there, and they
 *    all share the card's one set of settings.
 *  - Apply makes a graded copy of every picture wired in, at full size,
 *    from the settings on the card. Every Apply makes new pictures; the
 *    earlier ones stay.
 *  - The settings stay on the card until somebody changes them. A new card
 *    from the rail starts with nothing changed.
 *  - The card's own name, in its corner: double-click to write one.
 *    "Default" until somebody does.
 */

export const GRADE_DEFAULT_NAME = 'Default'

export interface GradeNodeData extends Record<string, unknown> {
  skillRef: string
  name: string
  glyph: string
  glow: string
  open?: boolean
  /** The settings: every slider, curve, wheel and mask. */
  grade?: GradeSettings
  /** What this card is called, in its corner. */
  gradeName?: string
  /** Applying now, and how far it has got ("2 / 5"). */
  running?: boolean
  progress?: string
  error?: string

  /* handed in by the board, never stored */
  /** The wired pictures' thumbnails; their count is how many are wired. */
  inputs?: string[]
  onEdit?: (patch: Partial<GradeNodeData>) => void
  onAdjust?: () => void
  onRun?: () => void
}

export const GradeNode = memo(function GradeNode({ data, selected }: NodeProps) {
  const card = data as GradeNodeData
  const isOpen = card.open !== false
  const running = card.running === true
  const inputs = card.inputs ?? []
  const wired = inputs.length
  const changes = card.grade ? changesIn(normaliseGrade(card.grade)) : 0
  const label = card.gradeName?.trim() || GRADE_DEFAULT_NAME
  const edit = (patch: Partial<GradeNodeData>) => card.onEdit?.(patch)

  const [naming, setNaming] = useState(false)
  const [draft, setDraft] = useState('')
  const field = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (naming) field.current?.select()
  }, [naming])
  const finishName = (keep: boolean) => {
    setNaming(false)
    if (keep) edit({ gradeName: draft.trim() || GRADE_DEFAULT_NAME })
  }

  return (
    <div
      className={[
        'cnode',
        'pnode',
        'gnode',
        isOpen ? 'pnode--open' : '',
        selected ? 'cnode--selected' : '',
        wired > 0 ? 'cnode--lit' : '',
        running ? 'cnode--running' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{ '--tile-glow': card.glow } as React.CSSProperties}
    >
      <Handle type="target" id="in" position={Position.Left} className="cnode__port" />
      <Handle type="source" id="out" position={Position.Right} className="cnode__port" />

      <div className="pnode__head">
        <span className="cnode__glyph" aria-hidden="true">
          <SkillGlyph glyph={card.glyph} />
        </span>
        <span className="cnode__name">{card.name}</span>
        {naming ? (
          <input
            ref={field}
            className="gnode__label gnode__label--edit nodrag"
            value={draft}
            maxLength={40}
            aria-label="Name of this photo adjust"
            onChange={(event) => setDraft(event.target.value)}
            onBlur={() => finishName(true)}
            onDoubleClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => {
              if (event.key === 'Enter') finishName(true)
              if (event.key === 'Escape') finishName(false)
            }}
          />
        ) : (
          <span
            className={`gnode__label nodrag${card.gradeName && card.gradeName !== GRADE_DEFAULT_NAME ? ' gnode__label--named' : ''}`}
            title="Double-click to name this grade"
            onDoubleClick={(event) => {
              event.stopPropagation()
              setDraft(label)
              setNaming(true)
            }}
          >
            {label}
          </span>
        )}
        <button
          type="button"
          className="pnode__toggle nodrag"
          onClick={() => edit({ open: !isOpen })}
          title={isOpen ? 'Fold this card' : 'Unfold this card'}
          aria-label={isOpen ? 'Fold this card' : 'Unfold this card'}
        >
          {isOpen ? '−' : '+'}
        </button>
      </div>

      {isOpen ? (
        <div className="pnode__body">
          <p className="gnode__summary">
            {wired === 0 ? 'No pictures wired in' : `${wired} picture${wired === 1 ? '' : 's'}`}
            {' · '}
            {changes === 0 ? 'nothing changed yet' : `${changes} change${changes === 1 ? '' : 's'}`}
          </p>
          {card.error ? <p className="pnode__error">{card.error}</p> : null}
        </div>
      ) : null}

      {isOpen || running ? (
        <div className="gnode__actions">
          <button
            type="button"
            className="gnode__adjust nodrag"
            onClick={() => card.onAdjust?.()}
            disabled={wired === 0 || running}
            title={wired === 0 ? 'Connect a picture first' : 'Open the full-screen photo adjust'}
          >
            Adjust
          </button>
          <button
            type="button"
            className="cnode__run pnode__run nodrag"
            onClick={() => card.onRun?.()}
            disabled={wired === 0 || running}
            title={wired === 0 ? 'Connect a picture first' : 'Make a graded copy of every picture wired in, at full size'}
          >
            {running ? `Applying${card.progress ? ` ${card.progress}` : ''}…` : (
              <>
                Apply
                <span className="pnode__credits">free</span>
              </>
            )}
          </button>
        </div>
      ) : null}

      {!isOpen && card.error ? <span className="cnode__error">{card.error}</span> : null}
    </div>
  )
})
